import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 md:grid-cols-2" }
const _hoisted_4 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_5 = { class: "mt-1 text-center text-3xl font-semibold text-gray-800" }
const _hoisted_6 = { class: "w-full border-collapse text-sm" }
const _hoisted_7 = { class: "sticky top-0 bg-gray-200 text-gray-800" }
const _hoisted_8 = { class: "align-center p-4" }
const _hoisted_9 = { class: "align-center p-4 text-center" }
const _hoisted_10 = { class: "p-4 text-center align-middle" }
const _hoisted_11 = { class: "p-4 text-center align-middle" }
const _hoisted_12 = { class: "align-center p-4 text-center" }
const _hoisted_13 = { class: "align-center p-4 text-center" }

import { ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
import { useFilterStore, useUiStore } from '@/stores';
import { AgentDeviceStatusHistoryReportService, toastServiceError } from '@/services';
import { formatSecToTime } from '@/utils/datetime';
import {
    Permission,
    UserDeviceType,
    AgentDeviceStatusHistoryReportServiceGetInfoResult,
} from '@/types';

import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import { DesktopComputerIcon } from '@heroicons/vue/solid';
import { DeviceMobileIcon } from '@heroicons/vue/outline';


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentDeviceReportPage',
  setup(__props) {

const filterStore = useFilterStore();
const uiStore = useUiStore();

const dateRange = computed({
    get: () => filterStore.dateRange,
    set: (range: Array<Date>) => filterStore.setDateRange(range),
});

const levelFilter = computed(() => filterStore.level);
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const agents = ref<AgentDeviceStatusHistoryReportServiceGetInfoResult[]>([]);
const totalTimePaused = ref(0);
const totalTimeCall = ref(0);

const tableHeaders = [
    { key: 'name', label: 'report.agentDeviceStatusHistory.details.userName', align: 'left' },
    {
        key: 'extension',
        label: 'report.agentDeviceStatusHistory.details.extension',
        align: 'center',
    },
    {
        key: 'queues',
        label: 'report.agentDeviceStatusHistory.details.queues',
        align: 'center',
    },
    {
        key: 'totalConnectedTime',
        label: 'report.agentDeviceStatusHistory.details.totalTimeOnline',
        align: 'center',
    },
    {
        key: 'totalPauseTime',
        label: 'report.agentDeviceStatusHistory.details.totalTimePaused',
        align: 'center',
    },
    {
        key: 'totalCallDuration',
        label: 'report.agentDeviceStatusHistory.details.totalTimeInCall',
        align: 'center',
    },
];

const getDeviceIcon = (deviceType: string) =>
    ({
        [UserDeviceType.MOBILE]: DeviceMobileIcon,
        [UserDeviceType.DESKTOP]: DesktopComputerIcon,
        [UserDeviceType.WEB]: SVGChromeLogo,
        [UserDeviceType.EXTERNAL]: SVGDeviceExternal,
    })[deviceType];

const getReportData = async () => {
    const params = {
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
    };

    try {
        setLoading(true);
        const { data } = await AgentDeviceStatusHistoryReportService.getInfo({ params });

        agents.value = data;
        totalTimePaused.value = data.reduce((acc, curr) => acc + (curr.totalPauseTime || 0), 0);
        totalTimeCall.value = data.reduce((acc, curr) => acc + (curr.totalCallDuration || 0), 0);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

watch([levelFilter, dateRange], getReportData);
onMounted(getReportData);

return (_ctx: any,_cache: any) => {
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiPanel = _resolveComponent("UiPanel")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('report.agentDeviceStatusHistory.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: ""
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    _createElementVNode("dl", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList({ totalTimePaused: totalTimePaused.value, totalTimeCall: totalTimeCall.value }, (value, key) => {
        return (_openBlock(), _createBlock(_component_UiPanel, { key: key }, {
          default: _withCtx(() => [
            _createElementVNode("dt", _hoisted_4, _toDisplayString(_ctx.$t(`report.agentDeviceStatusHistory.${key}`)), 1),
            _createElementVNode("dd", _hoisted_5, _toDisplayString(_unref(formatSecToTime)(value)), 1)
          ]),
          _: 2
        }, 1024))
      }), 128))
    ]),
    _createVNode(_component_UiPanel, { "no-padding": "" }, {
      default: _withCtx(() => [
        _createElementVNode("table", _hoisted_6, [
          _createElementVNode("thead", _hoisted_7, [
            _createElementVNode("tr", null, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tableHeaders, (header) => {
                return _createElementVNode("th", {
                  key: header.key,
                  class: _normalizeClass(["p-4", header.align === 'center' ? 'text-center' : 'text-left'])
                }, _toDisplayString(_ctx.$t(header.label)), 3)
              }), 64))
            ])
          ]),
          _createElementVNode("tbody", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(agents.value, (agent, idx) => {
              return (_openBlock(), _createElementBlock("tr", {
                key: idx,
                class: "even:bg-gray-100"
              }, [
                _createElementVNode("td", _hoisted_8, _toDisplayString(agent.name), 1),
                _createElementVNode("td", _hoisted_9, _toDisplayString(agent.extension), 1),
                _createElementVNode("td", _hoisted_10, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(agent.queues, (queue) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: queue.id,
                      class: "flex items-center justify-center gap-1"
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.$can(_unref(Permission).VIEW_QUEUE) ? 'RouterLink' : 'span'), {
                        to: {
                                        name: 'ViewQueueById',
                                        params: {
                                            id: queue?.id,
                                        },
                                    },
                        class: "text-primary"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(queue.name), 1)
                        ]),
                        _: 2
                      }, 1032, ["to"]))
                    ]))
                  }), 128))
                ]),
                _createElementVNode("td", _hoisted_11, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(agent.userDevices, (device) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: device.userDeviceId,
                      class: "flex items-center justify-center gap-1"
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(getDeviceIcon(device.deviceType)), { class: "h-4 w-4" })),
                      _createElementVNode("span", null, _toDisplayString(_unref(formatSecToTime)(device.totalConnectedTime, true)), 1)
                    ]))
                  }), 128))
                ]),
                _createElementVNode("td", _hoisted_12, _toDisplayString(_unref(formatSecToTime)(agent.totalPauseTime, true)), 1),
                _createElementVNode("td", _hoisted_13, _toDisplayString(_unref(formatSecToTime)(agent.totalCallDuration, true)), 1)
              ]))
            }), 128))
          ])
        ])
      ]),
      _: 1
    })
  ]))
}
}

})